import React from 'react'
import styled from '@emotion/styled'
import { breakpoints } from '../../constants'
import { fontSize2XSmall, fontSize3XSmall, fontSizeMedium, fontSizeXLarge, highlightBlueBase } from '../../constants/principles'
import { BodyDivFlex, FlexDiv, FlexRowDiv } from '../generic/Basics'
import CertificateHeader from './CertificateHeader'
import VehicleDetailsModal from './VehicleDetailsModal'
import LegalText from '../landing/LegalText'

const Vehicle = ({ year, make, model, trim, setShowModal, prospectResult, isAdmin }) => {
  const vehicleDescProspect = prospectResult && prospectResult.vehicle && prospectResult.vehicle.vehicleDescription && prospectResult.vehicle.vehicleDescription.vehicle
  const vehicleDesc = `${year && year.displayName} ${make && make.displayName} ${model && model.displayName} ${trim && trim.displayName}`
  return (
    <StartAlignDiv>
      <TextDiv fontSize={fontSize2XSmall}>
        {vehicleDescProspect || vehicleDesc}
      </TextDiv>
      <VehicleDetailsModal vehicleInfo={prospectResult} setShowModal={setShowModal} isAdmin={isAdmin} />
      <StyledLink onClick={() => setShowModal('vehicleDetails')}>Review vehicle details</StyledLink>
    </StartAlignDiv>
  )
}

const ProspectInfo = ({ prospectResult, prospectId, urlState }) => {
  const prospectIdParam = urlState && urlState.pa && urlState.pa[0] && urlState.pa[0].indexOf('prospect') === 0 && urlState.pa[1]
  const contacInfo = prospectResult && prospectResult.contactInfo
  const emailCustomerInfo = contacInfo && contacInfo.email
  const zipCustomerInfo = contacInfo && contacInfo.zip
  return (
    <>
      <SmallTextDiv data-testid={`oc-txt-offer-id`}>Offer ID: {prospectId || prospectIdParam}</SmallTextDiv>
      <SmallTextDiv data-testid={`oc-txt-offer-email`}>Email: {emailCustomerInfo}</SmallTextDiv>
      <SmallTextDiv data-testid={`oc-txt-offer-zipcode`}>Zip Code: {zipCustomerInfo}</SmallTextDiv>
    </>
  )
}

export default ({ setShowModal, updateGlobalState, dealerIsDirectToKBBv2, year, make, model, trim, isAdmin, prospectId, prospectResult, urlState, kbbUrl }) => {
  return (
    <BodyDivFlex>
      <CertificateHeader setShowModal={setShowModal} updateGlobalState={updateGlobalState} dealerIsDirectToKBBv2={dealerIsDirectToKBBv2} />
      <MainDiv>
        <NoOfferMainDiv>
          <MediumHeaderText>Your Offer is in progress but unexpectedly delayed. We'll email it to you as soon as it's available.</MediumHeaderText>
          Thank you for your patience.
        </NoOfferMainDiv>
        <FullWidth>
          <Vehicle {...{ year, make, model, trim, setShowModal, isAdmin, prospectResult }} />
          <EndAlignDiv>
            <ProspectInfo prospectResult={prospectResult} prospectId={prospectId} urlState={urlState} />
            <Questions data-testid={`oc-txt-kbb-question`}>Questions about the Instant Cash Offer program? Call <StyledA href="tel:866-559-5268">(866) 559-5268</StyledA></Questions>
          </EndAlignDiv>
        </FullWidth>
      </MainDiv>
      <LegalText dealerIsDirectToKBBv2={dealerIsDirectToKBBv2} kbbUrl={kbbUrl} isLandingPage={true} />
    </BodyDivFlex>
  )
}

const MainDiv = styled(FlexRowDiv)`
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  padding-top: 24px;
  align-items: center;
  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-start;
    flex-direction: column;
    flex-wrap: nowrap;
  }
`
const NoOfferMainDiv = styled.div`
  font-weight: 'normal';
  font-size: ${fontSize2XSmall};
  text-align: center;
  width: 100%;
  @media (min-width: ${breakpoints.lg}px) {
    text-align: left;
  }
`
const HeaderText = styled.div`
  font-size: ${fontSizeXLarge};
  font-weight: bold;
  padding-bottom: 16px;
  text-align: center;
  @media (min-width: ${breakpoints.lg}px) {
    text-align: initial;
  }
  line-height: 1.25;
  width: 100%;
`
const MediumHeaderText = styled(HeaderText)`
  font-size: ${fontSizeMedium};
  padding-bottom: ${p => (p.paddingBottom ? p.paddingBottom : '32px')};
  max-width: 840px;
  text-align: ${p => (p.textAlign ? p.textAlign : 'center')};
`
const FullWidth = styled(FlexRowDiv)`
  margin-top: 70px;
  margin-bottom: 200px;
  flex-direction: column;
  width: ${p => p.width || '100%'};
  justify-content: space-between;
  align-items: center;
  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-start;
    flex-direction: column;
  }
`
const StartAlignDiv = styled(FlexDiv)`
  align-items: ${p => (p.isDesktop ? p.align : 'center')};
  justify-content: 'center';
  @media (min-width: ${breakpoints.lg}px) {
    justify-content: flex-start;
    align-items: flex-start;
  }
`
const TextDiv = styled.div`
  font-weight: ${p => (p.fontWeight ? p.fontWeight : 'bold')};
  padding-top: ${p => (p.dealerIsDirectToKBB ? '4px' : `8px`)};
  font-size: ${p => p.fontSize};
  color: ${p => p.fontColor};
  text-align: ${p => (p.textAlign ? p.textAlign : 'center')};
  @media (min-width: ${breakpoints.lg}px) {
    text-align: ${p => (p.textAlign ? p.textAlign : `initial`)};
  }
`
const StyledLink = styled.div`
  color: ${highlightBlueBase};
  font-size: ${fontSize2XSmall};
  text-decoration: none;
  :hover {
    text-decoration: underline;
  }
  padding-bottom: 16px;
  cursor: pointer;
  @media (min-width: ${breakpoints.lg}px) {
    z-index: 1;
  }
`
const EndAlignDiv = styled.div`
  align-items: center;
  justify-content: 'center';
  @media (min-width: ${breakpoints.lg}px) {
    justify-content: flex-start;
    align-items: flex-end;
  }
`
const SmallTextDiv = styled.div`
  font-size: ${fontSize3XSmall};
  font-weight: normal;
  line-height: 18px;
  align-items: flex-end;
  justify-content: 'flex-start';
`
const Questions = styled.div`
  padding-top: 40px;
  font-size: ${fontSize2XSmall};
  font-weight: bold;
  text-align: center;
`
const StyledA = styled.a`
  color: ${highlightBlueBase};
  text-decoration: none;
  :hover {
    text-decoration: underline;
  }
`